/**
 * StatisticsSummary
 *
 * 요약 카드 (총 수입/지출/잔액/전기 대비)
 * 단일 카드로 통합하여 수입/지출/잔액을 vertical stack으로 표시
 */

import type { StatisticsSummary } from '../../types/Statistics'

interface StatisticsSummaryProps {
  summary: StatisticsSummary
}

export function StatisticsSummary({ summary }: StatisticsSummaryProps) {
  const formatAmount = (amount: number) => amount.toLocaleString()
  const formatChange = (change: number | undefined) => {
    if (change === undefined) return null
    const sign = change >= 0 ? '+' : ''
    return `${sign}${change.toFixed(1)}%`
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
      <div className="space-y-3">
        {/* 수입 */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500 dark:text-gray-400">수입</span>
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-income-fixed">
              {formatAmount(summary.totalIncome)}원
            </span>
            {summary.incomeChange !== undefined && (
              <span className={`text-xs ${
                summary.incomeChange >= 0 ? 'text-income-fixed' : 'text-expense-fixed'
              }`}>
                {formatChange(summary.incomeChange)}
              </span>
            )}
          </div>
        </div>

        {/* 지출 */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500 dark:text-gray-400">지출</span>
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-expense-fixed">
              {formatAmount(summary.totalExpense)}원
            </span>
            {summary.expenseChange !== undefined && (
              <span className={`text-xs ${
                summary.expenseChange >= 0 ? 'text-expense-fixed' : 'text-income-fixed'
              }`}>
                {formatChange(summary.expenseChange)}
              </span>
            )}
          </div>
        </div>

        {/* 잔액 (구분선 추가) */}
        <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-3">
          <span className="text-sm text-gray-500 dark:text-gray-400">잔액</span>
          <div className="flex items-center gap-2">
            <span className={`text-lg font-bold ${
              summary.netAmount >= 0 ? 'text-income-fixed' : 'text-expense-fixed'
            }`}>
              {formatAmount(summary.netAmount)}원
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {summary.transactionCount}건
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
